














































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { Pallet, Box, Layer, ProgramSettings, LayerPattern, RobotTypeEnum } from '@/model'
import { Pallet3D } from '@/libs/pallet3D'
import { getSimpleBar } from '@/libs/scroll'
import 'simplebar/dist/simplebar.min.css'

@Component({})
export default class SimpleForm extends Vue {
  @Prop({ required: true }) private value!: Pallet
  @Prop({ required: true }) private box!: Box
  @Prop({ required: true }) private settings!: ProgramSettings
  @Prop({ required: true }) private parentBus!: Vue

  private pallet: Pallet = JSON.parse(JSON.stringify(this.value))
  private valid: boolean = false
  private view?: Pallet3D
  private minLayersCount: number = 2
  private layersCount: number = this.pallet.layers!.length || this.minLayersCount
  private isNewPattern: boolean = this.pallet.layers!.length < 1

  get reversedLayers (): Layer[] {
    // console.log('getting reversed layers')
    const layers = this.pallet.layers
    if (layers) {
      return layers.slice().reverse()
    }
    return []
  }

  set reversedLayers (value: Layer[]) {
    if (this.view) {
      this.adjust_pallet3d(value.length)
      this.view.batchLayers(true)
      this.view.rerender()
    }
  }

  get form (): any {
    return this.$refs.form as any
  }

  get nonEmptyLayerPatterns (): LayerPattern[] {
    if (!this.pallet.layerPatterns) {
      return []
    }
    return this.pallet.layerPatterns.filter(p => p.boxPositions != null && p.boxPositions.length > 0)
  }

  get maxLayersCount (): number {
    let baseHeight = 2065 // base height for RI20_FANUC and RI20_KUKA
    if (this.settings.robotType === RobotTypeEnum.RC10_UR) {
      baseHeight = 1200 // base height for UR
    }
    const additionalHeight = this.settings.additionalPedestalHeight || 0
    const palletHeight = this.pallet.height || 0
    // remove pallet height from usable height
    const usableHeight = baseHeight + additionalHeight - palletHeight
    const boxHeight = this.box.height!
    const maxLayers = Math.floor(usableHeight / boxHeight)
    // return maxLayers
    return 25
  }

  private rules: object = {
    required: (v: any) => (v !== null && v !== undefined && v !== '') || this.localText('general.required'),
    thicknessRequired: (v: any) => this.validateThickness(v) || this.localText('general.required'),
    minLayers: (v: any) => ((!v) || (v >= this.minLayersCount)) || `${this.localText('general.atleast')} ${this.minLayersCount}`,
    maxLayers: (v: any) => ((!v) || (v <= this.maxLayersCount)) || `${this.localText('general.maximumPal')} ${this.maxLayersCount}`,
    minThickness: (v: any) => ((!v) || (v >= 0)) || `${this.localText('general.atleast')} ${0}`,
    maxThickness: (v: any) => ((!v) || (v <= 100)) || `${this.localText('general.maximumPal')} ${100}`
  }

  @Watch('pallet.interlayerSheetPositions')
  private interlayerSheetPositionsWatcher(value: boolean[]) {
    this.form.validate()
  }

  // Watcher to emit valid state to "next" button in stepper
  @Watch('valid')
  private validWatcher (value: boolean) {
    this.$emit('validate', value)
  }

  @Watch('reversedLayers', { deep: true })
  private reversedLayersWatcher (value: Layer[]) {
    // console.log('reversed layers in layerForm has changed: ', JSON.stringify(this.reversedLayers), this.view)
    if (this.view) {
      // this.adjust_pallet3d(value.length) // extra scroll in not needed
      this.view.batchLayers(true)
      this.view.rerender()
    }
  }

  @Watch('layersCount')
  private layersCountWatcher(value: number) {
    this.populateLayers()
    this.removeInvalidLayerReferences()
    this.reversedLayers = this.pallet.layers!
  }

  private validateThickness(v: any) {
    if (this.isAnyInterlayerSheet()) { // If any interlayer is true
      return v != null && v !== ''
    } else {
      return true
    }
  }

  private isAnyInterlayerSheet() {
    return this.pallet!.interlayerSheetPositions!.some(p => p)
  }

  private mounted () {
    this.parentBus.$on('stepper_next', this.next)
    this.parentBus.$on('stepper_previous', this.previous)
    this.populateLayers()
    this.removeInvalidLayerReferences()
    this.pallet3d()
    this.valid = this.validateThickness(this.pallet.interlayerThickness)
  }

  private beforeDestroy () {
    this.parentBus.$off('stepper_next', this.next)
    this.parentBus.$off('stepper_previous', this.previous)
  }

  private next () {
    // console.log('SimpleFormF.next: ', JSON.stringify(this.pallet.layers))
    const obj = {
      interlayerSheetPositions: this.pallet.interlayerSheetPositions,
      layers: this.pallet.layers
    } as Partial<Pallet>
    if (this.pallet.interlayerThickness != null) {
      obj.interlayerThickness = this.pallet.interlayerThickness
    }
    this.$emit('next', obj)
  }

  private previous () {
    this.$emit('previous')
  }

  private removeInvalidLayerReferences () {
    const validLayerPatternNames = this.nonEmptyLayerPatterns.map(p => p.name)
    const bIsValid = validLayerPatternNames.includes('B')
    const zIsValid = validLayerPatternNames.includes('Z')
    // Set layer pattern to 'A' for all layers that reference patterns (e.g. 'B', 'Z') that are empty
    for (let i = 0; i < this.pallet.layers!.length; i++) {
      const l = this.pallet.layers![i]
      if (l.layerPattern === 'Z' && !zIsValid) {
        l.layerPattern = (i % 2 === 0) ? 'A' : 'B'
        // console.log('changed layer', l.number, 'from Z to', l.layerPattern)
      }
      if (l.layerPattern === 'B' && !bIsValid) {
        l.layerPattern = 'A'
        // console.log('changed layer', l.number, 'from B to A')
      }
    }
  }

  // TODO: Make sure we omit any layerpatterns that does not have any boxes or is "saved"
  private pallet3d () {
    // console.log('creating pallet')
    this.view = new Pallet3D('pallet3d', this.box, this.pallet, this.settings, 0xfafafa, { h: 530, w: 490 }, 0)
    this.view.batchLayers(true)
    setTimeout(() => {
      this.adjust_pallet3d(this.pallet.layers!.length)
    }, 30)
  }

  private scroll_pallet3d () {
    const elem = document.getElementById('wrapper3d')
    if (elem) {
      const simpleBar = getSimpleBar(elem)
      const scroll = simpleBar.getScrollElement()
      scroll.scrollTop = scroll.scrollHeight
    }
  }

  private adjust_pallet3d (size: number) {
    const wrapper3d = document.getElementById('wrapper3d')
    const elem = document.getElementById('pallet3d')
    if (elem) {
      this.scroll_pallet3d()
    }
  }

  private updateIntersheet (event: any, layerNumber: number) {
    if (!this.view) {
      return false
    }
    if (event) {
      this.view!.addInterSheetByLayer(layerNumber)
    } else {
      this.view!.removeInterSheetByLayer(layerNumber)
    }
    this.view!.rerender()
  }

  private removeLayer (layer: Layer) {
    this.$emit('removeLayer', layer)
  }


  // Compares updated layersCount with length of pallet.layers to check if we should add or remove layers
  // The number starts at 1 since that's indicating the bottom layer
  // side-effect: Also updates interlayerSheetPositions
  private populateLayers () {
    while (this.pallet.layers!.length < this.layersCount) {
      // we need to add a layer to reach new layersCount
      const num = this.pallet.layers!.length + 1
      const layerPattern = (num % 2 === 0) ? 'B' : 'A'
      const obj = {
        number: num,
        layerPattern: layerPattern
      }
      this.pallet.layers!.push(obj)
    }
    while (this.pallet.layers!.length > this.layersCount) {
      // we need to remove a layer to reach new layersCount
      this.pallet.layers!.pop()
    }
    // use z as top layer if new layer count > 2 and this is a new pattern or the previous layer settings had z as top layer
    const previousLayerCount = (this.value.layers) ? this.value.layers.length : 0
    if (this.layersCount > 2 && (this.isNewPattern || (previousLayerCount > 2 && this.value.layers![previousLayerCount - 1].layerPattern === 'Z'))) {
      this.pallet.layers![this.layersCount - 1].layerPattern = 'Z'
    }
    this.populateInterlayerSheetPositions()
  }

  private populateInterlayerSheetPositions () {
    // ensure there is always one more entry in interlayerSheetPositions than in layers
    const targetLength = this.pallet.layers!.length + 1
    while (this.pallet.interlayerSheetPositions!.length < targetLength) {
      this.pallet.interlayerSheetPositions!.push(false)
    }
    while (this.pallet.interlayerSheetPositions!.length > targetLength) {
      this.pallet.interlayerSheetPositions!.pop()
    }
  }

  private localText (b: string): string {
    return String(this.$t(b))
  }
}
